import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  ParamMap,
  Router,
  RouterEvent,
} from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { filter } from 'rxjs';
import {
  UiBlueprintDesignerControllerService,
  UiDropdownsControllerService,
  UiStackControllerService,
} from 'src/app/cc-api/services';
import {
  Branch,
  BreadCrumbConfig,
  PagePath,
} from 'src/app/models/breadcrumb.model';
import { ContextUpdatesService } from 'src/app/services/context-updates.service';
import { SidebarStateService } from 'src/app/services/sidebar-state.service';
import { SortOptionsService } from 'src/app/services/sort-options.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { UserService } from 'src/app/services/user.service';
import { CustomToastService } from 'src/app/pages/custom-toast/custom-toast.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { BlueprintNameLabelService } from 'src/app/services/blueprint-name-label.service';
import { ContextWidgetInstancesService } from '../context-widget/services/context-widget-instances.utils.service';
import { CacheDataService } from 'src/app/services/cache-data.service';
import { settingsRoutes } from 'src/app/constants/settings-route';
import { Stack } from 'src/app/cc-api/models';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';

@Component({
  selector: 'app-breadcrumbs-v2',
  templateUrl: './breadcrumbs-v2.component.html',
  styleUrls: ['./breadcrumbs-v2.component.scss'],
})
export class BreadcrumbsV2Component implements OnInit, OnDestroy {
  URL: string; // Current URL, breadcrumb = function(URL)
  URLParams: ActivatedRouteSnapshot; // URL object which has all the params
  title: string; // Title of the page coming from the data object from the router
  breadcrumbConfig: BreadCrumbConfig; // Breadcrumb object (currently active)
  nextBreadcrumbConfig: BreadCrumbConfig; // Breadcrumb object (next breadcrumb config)
  blueprintEnvironments: any; // Map of all clusters mapped under their respective blueprint name
  contextSwitcherOpen = false; // State of the context switcher (open/closed)
  allBlueprints_: any[]; // List of all blueprints [Fetched using the getStacks API], needed because allBlueprints wouldn't have the blueprints with #zero environments
  allBlueprints: any[]; // List of all blueprints [Created from the keys of the getAllCluster API response]
  blueprintTemplateMap: {} = {};
  allEnvironments: any[]; // List of all environments which are under the current blueprint
  allResourceTypes: any[]; // List of all resource types inside an environment of a blueprint
  allResources: any[]; // List of resources present inside the selected resource type of an environment of a blueprint
  routerSubscription$: Subscription; // Subscription of router for unsubscribing whenever required
  environmentIDMap: any; // Since the URL uses the environment ID and the breadcrumb maintains the name of the
  // environment (environmentIDMap[environmentName] => environmentID)
  currentResourceMap: any; // Map of the currently needed resource types and resources
  resourceTypeSubscription$: Subscription; // Subscription for cancelling API calls whenever they become redundant
  fetchingResourceTypes = false; // Status indicator for get resource API for showing the loader whenever needed
  noResourcesError = false; // Error indicator for displaying if the resources do not exist for the selected [Blueprint, Environment]
  isValidBreadcrumb: boolean; // State of validity for breadcrumb to enable/disable the 'Apply' button
  pagePath: PagePath; // Stores data for the path of the page (other than the context switcher items)
  activeComponentName: string; // The name of the component which is currently rendered inside the primary router
  activeComponentRoute: ActivatedRoute; // The route of the component which is currently rendered inside the primary router
  activeComponentURLStructure: string; // The structure of the URL of the component which is currently rendered inside the primary router
  preventSanitisation: boolean = false; // Forces URL to stay how it is and prevents sanitisation of URL
  lastContextChangesUsingBreadcrumbs: boolean = false; // Flag to check whether the last context changed took place because of the context switcher or not
  incorrectURL: boolean = false;
  incorrectURLTooltip: string;
  incorrectURLRedirectLink: string;
  filteredNgModelOptions$: {
    // Observables for all the keys of a blueprint to filter and display only the searched for names inside the respective container
    blueprint: Observable<string[]>;
    environment: Observable<string[]>;
    resourceType: Observable<string[]>;
    resource: Observable<string[]>;
  } = {} as any;
  allowedClusterIds: Set<string> = new Set<string>(); //Stores clusterIds of all the cluster a user has access to.
  allowedStackNames: Set<string> = new Set<string>(); //Stores stackNames of all the stacks a user has access to.
  previousResourceType;
  previousResource;
  resourceErrTimeout: any;
  resourceErrShown: boolean = false;
  branchSelectorNeeded: boolean = false;
  ENVIRONMENT = 'ENVIRONMENT';
  RESOURCE = 'RESOURCE';
  blueprintsNameLabelMap = {};
  contextWidgetPresent: boolean = false;
  allStacksObj: Stack[];

  @ViewChild('environmentSearchTerm', { static: false })
  environmentSearchTerm: ElementRef;
  @ViewChild('resourceTypeSearchTerm', { static: false })
  resourceTypeSearchTerm: ElementRef;
  @ViewChild('resourceSearchTerm', { static: false })
  resourceSearchTerm: ElementRef;
  constructor(
    private router: Router,
    private uiStackControllerService: UiStackControllerService,
    private uiDropdownsControllerService: UiDropdownsControllerService,
    public contextUpdateService: ContextUpdatesService,
    private sortOptionsService: SortOptionsService,
    private sidebarStateService: SidebarStateService,
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private blueprintDesignerService: UiBlueprintDesignerControllerService,
    private contextWidgetInstancesService: ContextWidgetInstancesService,
    private route: ActivatedRoute,
    public featureFlagsService: FeatureFlagsService,
    private customToastService: CustomToastService,
    public blueprintNameLabelService: BlueprintNameLabelService,
    private cacheDataService: CacheDataService,
  ) {
    this.setBreadcrumb();
  }

  // Sets event listener for receiving signals from other components for refreshing the context switcher if needed
  // Eg. If a new blueprint or an environment is created, we'll have to add new items
  ngOnInit(): void {
    this.contextUpdateService.refreshContextSwitcher.subscribe(
      (signal: any) => {
        if (signal.refresh) {
          this.setBreadcrumb(signal.extras);
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.routerSubscription$?.unsubscribe();
    if (this.resourceErrTimeout) {
      clearTimeout(this.resourceErrTimeout);
    }
  }

  // Waits to receive the data which is needed to create the [Blueprint->Environments] map
  // Sets the listener for catching URL change events
  // The [Blueprint->Environments] map needs to be updated every time the data is updated in the DB against any blueprint or environment
  async setBreadcrumb(extras?: any) {
    await this.getClusterAndBlueprintMapping();
    await this.getAllBlueprints();
    this.contextUpdateService.emitBranchDataLoadedSignal();
    if (!this.routerSubscription$) {
      this.initURLChangeEventListener();
    }
    this.initAllBlueprints();
    this.updateBreadcrumbConfigFor(this.router.url);
    this.contextUpdateService.notifyContextSwitcherUpdate({ extras: extras });
  }

  dependentEnvironments = [];
  // Makes a call for fetching all the environments and maps them by their cluster name
  getClusterAndBlueprintMapping() {
    return new Promise((resolve) => {
      this.uiStackControllerService
        .getAllClustersUsingGET({
          page: 0,
          perPage: 20000,
          sortBy: 'ID',
        })
        .subscribe(async (envs) => {
          this.blueprintEnvironments = {};
          this.environmentIDMap = {};
          this.allowedClusterIds = new Set(
            this.userService?.user?.allowedClusterIds,
          );
          this.allowedStackNames = new Set(
            this.userService?.user?.allowedStackNames,
          );
          let envsList = envs?.content;
          if (this.allowedStackNames?.size !== 0) {
            envsList = envsList?.filter((env) =>
              this.allowedStackNames.has(env?.stackName),
            );
          }

          if (this.allowedClusterIds.size !== 0) {
            envsList?.forEach((cluster) => {
              const stackName = cluster?.stackName;
              if (this.allowedClusterIds.has(cluster?.id)) {
                // Two blueprints can have same environment name. So concatenating blueprint with environment name to get uniqueID
                let key = `${stackName}_${cluster?.name}`;
                this.environmentIDMap[key] = cluster?.id;
                if (this.blueprintEnvironments.hasOwnProperty(stackName)) {
                  this.blueprintEnvironments[stackName].push(cluster);
                } else {
                  this.blueprintEnvironments[stackName] = [cluster];
                }
              }
            });
          } else {
            envsList?.forEach((cluster) => {
              const stackName = cluster?.stackName;
              let key = `${stackName}_${cluster?.name}`;
              this.environmentIDMap[key] = cluster?.id;
              if (this.blueprintEnvironments.hasOwnProperty(stackName)) {
                this.blueprintEnvironments[stackName].push(cluster);
              } else {
                this.blueprintEnvironments[stackName] = [cluster];
              }
            });
          }
          this.dependentEnvironments = [];
          for (const stackName in this.blueprintEnvironments) {
            let envList = [];
            this.blueprintEnvironments[stackName]?.forEach((env) => {
              if (env?.baseClusterId?.length) {
                this.dependentEnvironments.push(`${stackName}:${env?.name}`);
              }

              envList.push({
                name: env?.name,
                id: env?.id,
              });
            });
          }
          resolve(true);
        });
    });
  }

  // Get all blueprints from getStack API to add the blueprints to allBlueprints which had no environments
  getAllBlueprints() {
    return new Promise((resolve) => {
      this.uiStackControllerService.getStacksUsingGET1().subscribe((stacks) => {
        // Set stacks in various service
        this.cacheDataService.setBlueprints(stacks);
        this.blueprintNameLabelService.setBlueprintNameLabelMap(stacks);

        this.allBlueprints_ = stacks.map((stack: any) => stack?.name);
        this.allBlueprints_.forEach((blueprint) => {
          if (!this.blueprintEnvironments.hasOwnProperty(blueprint)) {
            this.blueprintEnvironments[blueprint] = [];
          }
        });
        this.allStacksObj = stacks;
        stacks.forEach((stack) => {
          this.blueprintTemplateMap[stack?.name] = stack?.template;
          if (
            !this.contextUpdateService.getLastBranchForBlueprint(stack?.name) ||
            !this.featureFlagsService.getBranchSelectorFlag()
          ) {
            this.contextUpdateService.setLastOpenedBranchInLocalStorage(
              stack?.name,
              stack?.branch,
            );
          }
        });
        /*
         * Get local storage facets store
         * Iterate over blueprint environments data and add blueprints to the search data
         * Add environments to the search data by iterating over environments in a blueprints
         * Set the store to the local storage.
         * Get resources for each environment in blueprint
         */
        let store = this.localStorageService.getFacetsStore() || {};
        if (
          !store['searchData']?.length &&
          !this.featureFlagsService.isSaaSCP
        ) {
          store['searchData'] = settingsRoutes.filter(
            (setting) =>
              !setting?.data?.checkPermission ||
              this.checkUserAccessToPage(setting?.data?.roles),
          );
          let resourcePromiseList = [];
          for (let stack in this.blueprintEnvironments) {
            ['Overview', 'Delivery Pipeline'].forEach((routeType) => {
              store['searchData']?.push({
                type: 'STACK',
                name: stack,
                searchString: stack + '|' + routeType?.toLowerCase(),
                data: {
                  urlType: routeType,
                },
              });
            });
            this.blueprintEnvironments[stack].forEach((env) => {
              ['Environment Overview', 'Environment Releases'].forEach(
                (routeType) => {
                  store['searchData']?.push({
                    type: 'CLUSTER',
                    name: env?.name,
                    searchString:
                      stack + '|' + env?.name + '|' + routeType?.toLowerCase(),
                    data: {
                      stackName: stack,
                      clusterId: env?.id,
                      urlType: routeType,
                      dependent: env?.baseClusterId ? true : false,
                    },
                  });
                },
              );
              resourcePromiseList.push(
                this.getResources({
                  clusterId: env?.id,
                  stackName: stack,
                  clusterName: env?.name,
                  dependent: env?.baseClusterId ? true : false,
                }),
              );
            });
          }
          this.localStorageService.setFacetsStore(store);
          this.getAllResourcesForClusters(resourcePromiseList);
        }
        this.contextUpdateService.setAllBlueprints(this.allBlueprints_);
        resolve(true);
      });
    });
  }

  /*
   * Create promise list for all clusters
   * Call resource data api for all the clusters and get the data
   * Once the data is available, set the resources value in local storage
   */
  getAllResourcesForClusters(promiseList) {
    Promise.allSettled(promiseList).then((data) => {
      let store = this.localStorageService.getFacetsStore() || {};
      if (data?.length) {
        data.forEach((resourceData) => {
          if (resourceData?.status === 'fulfilled') {
            let resources = resourceData?.value?.resources;
            let clusterData = resourceData?.value?.clusterData;
            for (let resourceType in resources) {
              let resourceNames = resources[resourceType];
              const branchName = this.allStacksObj?.find(
                (stack) => stack?.name === clusterData?.stackName,
              )?.branch;
              resourceNames.forEach((name) => {
                store['searchData']?.push({
                  type: 'RESOURCE',
                  name: name,
                  searchString:
                    clusterData?.stackName +
                    '|' +
                    clusterData?.clusterName +
                    '|' +
                    resourceType +
                    '|' +
                    name +
                    '|' +
                    'configuration',
                  data: {
                    stackName: clusterData?.stackName,
                    branch: branchName,
                    clusterName: clusterData?.clusterName,
                    clusterId: clusterData?.clusterId,
                    dependent: clusterData?.dependent,
                    resourceType: resourceType,
                    urlType: 'Configuration',
                  },
                });
              });
            }
          }
        });
      }
      this.localStorageService.setFacetsStore(store);
    });
  }

  getResources(clusterData) {
    return new Promise((resolve, reject) => {
      let params = {
        clusterId: clusterData?.clusterId,
        excludeSubstackResources: false,
      };
      this.uiDropdownsControllerService
        .getAllResourcesUsingGET(params)
        .subscribe(
          (totalResources) => {
            resolve({
              resources: totalResources,
              clusterData: clusterData,
            });
          },
          (error) => {
            reject({ state: 'error' });
          },
        );
    });
  }

  // Sets all the blueprints the user can search from
  initAllBlueprints() {
    this.allBlueprints = this.sortOptionsService.sortList(
      Object.keys(this.blueprintEnvironments),
    );
    this.filteredNgModelOptions$.blueprint = of(this.allBlueprints);
  }

  // Sets all the environments the user can search from for a particular blueprint
  initAllEnvironmentsForBlueprint(blueprint: string) {
    this.allEnvironments = this.sortOptionsService.sortList(
      this.blueprintEnvironments[blueprint],
      'name',
    );
    this.contextUpdateService.setCurrentEnvironments(this.allEnvironments);
    this.filteredNgModelOptions$.environment = of(this.allEnvironments);
  }

  // Sets all the resources for a [Blueprint -> Environment] combination & for [Blueprint] in case on Blueprint Designer
  // Makes an API call to get the needed resources every time the environment is changed in the context switcher
  initResourceTypes(
    cluster: { name: string; ID: string },
    stackName,
    initResourcesFor?: string,
  ) {
    if (this.resourceTypeSubscription$) {
      this.resourceTypeSubscription$.unsubscribe();
    }
    this.fetchingResourceTypes = true;
    this.noResourcesError = false;
    if (!cluster?.ID) {
      let key = `${stackName}_${cluster?.name}`;
      cluster.ID = this.environmentIDMap[key];
    }
    let params = {
      clusterId: cluster?.ID,
      excludeSubstackResources: false,
    };
    if (stackName && cluster?.ID) {
      this.resourceTypeSubscription$ = this.uiDropdownsControllerService
        .getAllResourcesUsingGET({
          clusterId: cluster?.ID,
          excludeSubstackResources: false,
        })
        .subscribe(
          (resources) => {
            this.processResources(resources, stackName, initResourcesFor);
          },
          (error) => {
            this.setNoResourceContent();
          },
        );
    } else if (stackName) {
      this.resourceTypeSubscription$ = this.uiDropdownsControllerService
        .getAllResourcesByStackUsingGET({
          stackName: stackName,
          includeContent: true,
          excludeAddOns: false,
          branch:
            this.contextUpdateService.getLastBranchForBlueprint(stackName),
        })
        .subscribe(
          (resources) => {
            const resourceMap = {};
            resources.forEach((resource) => {
              if (resource?.resourceType?.toLowerCase() != 'unknown') {
                if (resourceMap?.hasOwnProperty(resource?.resourceType)) {
                  resourceMap[resource?.resourceType].push(
                    resource?.resourceName,
                  );
                } else {
                  resourceMap[resource?.resourceType] = [
                    resource?.resourceName,
                  ];
                }
              }
            });
            this.processResources(resourceMap, stackName, initResourcesFor);
          },
          (error) => {
            this.setNoResourceContent();
          },
        );
    } else {
      this.setNoResourceContent();
    }
  }

  setNoResourceContent() {
    this.currentResourceMap = {};
    this.allResourceTypes = [];
    this.filteredNgModelOptions$.resourceType = of([]);
    this.filteredNgModelOptions$.resource = of([]);
    this.fetchingResourceTypes = false;
    this.noResourcesError = true;
  }

  processResources(resources, stackName, initResourcesFor) {
    this.currentResourceMap = resources;
    this.allResourceTypes = this.sortOptionsService.sortList(
      Object.keys(resources),
    );
    this.filteredNgModelOptions$.resourceType = of(this.allResourceTypes);
    //If there is previously selected resource type value then set it.
    //If previously selected value is missing in the list then set first element from the dropdown
    if (this.previousResourceType) {
      this.nextBreadcrumbConfig.resourceType =
        this.allResourceTypes?.indexOf(this.previousResourceType) !== -1
          ? this.previousResourceType
          : this.allResourceTypes[0];
      this.initResources(this.nextBreadcrumbConfig?.resourceType);
      this.previousResourceType = null;
    }
    this.fetchingResourceTypes = false;
    if (initResourcesFor) {
      this.initResources(initResourcesFor);
    }
    this.contextUpdateService.setBlueprintResourceMapping(
      stackName,
      this.currentResourceMap,
    );
  }

  // Sets all the resources the user can search from for a particular resource type
  // If the resourceMap doesn't exist (happens only when the component is loaded for the first
  // time in the middle of the application somewhere with resource information needed), it makes a call to fetch
  // all the resource information first and then another call from that function is made back to this one once the data
  // is ready to be used. Basically, it goes in the first IF condition and exits when the data is not there and is then
  // called back and then the ELSE block is executed
  initResources(resourceType: string, currentConfig?: BreadCrumbConfig) {
    if (
      !this.currentResourceMap ||
      !Object.keys(this.currentResourceMap)?.length
    ) {
      this.initResourceTypes(
        { ID: null, name: currentConfig?.environment },
        currentConfig?.blueprint,
        resourceType,
      );
    } else {
      this.allResources = this.sortOptionsService.sortList(
        this.currentResourceMap[resourceType],
      );
      this.filteredNgModelOptions$.resource = of(this.allResources);
      if (this.previousResource) {
        this.nextBreadcrumbConfig.resource =
          this.allResources?.indexOf(this.previousResource) !== -1
            ? this.previousResource
            : this.allResources[0];
        this.updateBreadcrumbValidity();
      }
    }
  }

  // Listen to the URL change event with a subscription to the router
  // and then updates the breadcrumbs accordingly
  initURLChangeEventListener() {
    this.routerSubscription$ = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((routerEvent: RouterEvent) => {
        if (!this.isIntermediateURL(routerEvent.url)) {
          this.updateBreadcrumbConfigFor(routerEvent.url);
        }
      });
  }

  // Checks the hardcoded registry to determine whether the URL is an intermediate URL or not
  // and based on that if it should be processed or not for the context updates
  // Add else conditions inside to register edge-cases.
  isIntermediateURL(url: string): boolean {
    // Add more regex patterns in this array
    const patterns = [/.*\/blueprint-designer\/.*\/loading$/];

    for (const pattern of patterns) {
      if (pattern.test(url)) {
        return true;
      }
    }

    return false;
  }

  // Breadcrumb = Function of URL
  // This function is the starting point for preparing the breadcrumb from the URL
  // The URL which is passed to this function is the active URL
  // Page path is the address of the component we are on (determined using levels from the router config)
  // Every time the page is refreshed, we also need to reset the context switchers state to default it to it's active config
  async updateBreadcrumbConfigFor(URL) {
    this.URL = URL;
    this.pagePath = new PagePath();
    this.setRouteMetaData(this.router.routerState.snapshot.root);
    this.contextUpdateService.updateURLParams(this.URLParams);
    this.activeComponentURLStructure = this.getActiveRoutePathStructure(
      this.activeComponentRoute,
    );
    try {
      await this.processURL(this.URL);
      this.incorrectURL = false;
      this.nextBreadcrumbConfig = { ...this.breadcrumbConfig };
      this.contextSwitcherOpen = false;
      this.contextWidgetPresent =
        this.contextWidgetInstancesService.hasInstance();
      this.clearFilters();
    } catch (err) {
      this.redirectToAValidURL((err?.toString() as string)?.substring(7));
      this.incorrectURL = true;
      this.setBrokenURLFor((err?.toString() as string)?.substring(7));
    }

    // If the last context wasn't updated because of a change from the context switcher
    // and was instead updated because of a URL change or a change coming from a component directly
    // we need to send the update signal to the sidebar which we can only do by using the context updater service
    // and its 'optimistic' mode because that is what the sidebar subscribes too
    if (!this.lastContextChangesUsingBreadcrumbs) {
      this.updateCurrentContextInCookies(
        'currentContext',
        'optimistic',
        this.breadcrumbConfig,
      );
    }
    this.updateCurrentContextInCookies(
      'currentContext',
      'successful',
      this.breadcrumbConfig,
    );
    this.lastContextChangesUsingBreadcrumbs = false;
  }

  async redirectToAValidURL(errorAt: string) {
    this.displayBrokenContextToaster(errorAt);
    if (errorAt == 'blueprint') {
      // Redirect to the list of all blueprints
      this.router.navigate(['/home']);
    } else if (errorAt == 'environment') {
      // Redirect to all environments of the current blueprint
      this.router.navigate([`/stack/${this.URLParams?.params?.stackName}`]);
    } else if (errorAt == 'resource') {
      // Redirect to the resource center of the current BP > Env
      let redirectUrl = '';
      if (this.branchSelectorNeeded) {
        redirectUrl = `/stack/${this.URLParams?.params?.stackName}/blueprint-designer/${this.URLParams?.params?.branchName}/resource-list`;
      } else if (
        this.breadcrumbConfig?.resource &&
        !this.breadcrumbConfig?.environment
      ) {
        redirectUrl = `/stack/${
          this.URLParams?.params?.stackName
        }/blueprint-designer/${this.contextUpdateService.getLastBranchForBlueprint(
          this.URLParams?.params?.stackName,
        )}`;
      } else {
        redirectUrl = `/stack/${this.URLParams?.params?.stackName}/resources/cluster/${this.URLParams?.params?.clusterId}`;
      }
      this.router.navigate([redirectUrl]);
    } else if (errorAt == 'branch') {
      this.nextBreadcrumbConfig = this.breadcrumbConfig;
      this.nextBreadcrumbConfig.branch.selectedBranch =
        this.nextBreadcrumbConfig.branch.defaultBranch;
      this.applyBreadcrumbConfig();
    }
  }

  displayBrokenContextToaster(errorAt: string) {
    let errorMessage = `Invalid URL: ${
      errorAt?.charAt(0)?.toUpperCase() + errorAt?.slice(1)
    } does not exist, please select a valid ${errorAt}`;
    if (errorAt == 'branch') {
      // errorMessage =
      //   'Branch not available based on your settings. Request for Branch Selector feature to access other branches.';
    }
    if (errorMessage) {
      this.customToastService.error(errorMessage);
    }
  }

  setBrokenURLFor(property: string) {
    switch (property) {
      case 'blueprint':
        this.incorrectURLTooltip =
          'Invalid blueprint, click to check all available blueprints';
        this.incorrectURLRedirectLink = '/home';
        break;

      case 'environment':
        this.incorrectURLTooltip =
          'Invalid environment, click to check all available environments';
        this.incorrectURLRedirectLink =
          '/stack/' + this.URLParams?.params?.stackName;
        break;
    }
  }

  redirectToHandleIncorrectURL() {
    this.router.navigate([this.incorrectURLRedirectLink]);
  }

  // Emits a signal through the context updater service for the other components to know that the context has been updated
  updateContext(status: 'optimistic' | 'successful') {
    try {
      let currentContext = this.contextUpdateService.getContextFromLocalStorage(
        'currentContext',
      )
        ? (this.contextUpdateService.getContextFromLocalStorage(
            'currentContext',
          ) as BreadCrumbConfig)
        : ({} as BreadCrumbConfig);
      let previousContext =
        this.contextUpdateService.getContextFromLocalStorage('previousContext')
          ? (this.contextUpdateService.getContextFromLocalStorage(
              'previousContext',
            ) as BreadCrumbConfig)
          : ({} as BreadCrumbConfig);
      this.contextUpdateService.setContextUpdates(
        {
          currentContext: currentContext,
          previousContext: previousContext,
        },
        status,
      );
    } catch (error) {}
  }

  // Update the state of currentContext and previousContext in the cookies
  // Whenever the currentContext is updated, the previousContext would also be updated
  updateCurrentContextInCookies(
    type: 'currentContext' | 'previousContext',
    status: 'optimistic' | 'successful',
    context: BreadCrumbConfig | null,
  ) {
    let _context = {};
    if (context) {
      Object.keys(context).forEach((breadcrumbKey: string) => {
        if (breadcrumbKey === 'environment') {
          let key = context?.blueprint + '_' + context?.environment;
          // _context[breadcrumbKey] =
          //   this.environmentIDMap[context[breadcrumbKey]];
          _context[breadcrumbKey] = this.environmentIDMap[key];
        } else {
          _context[breadcrumbKey] = context[breadcrumbKey];
        }
      });
    } else {
      _context = `{}`;
    }

    if (type === 'currentContext') {
      let previousContext =
        this.contextUpdateService.getContextFromLocalStorage('currentContext');
      if (previousContext != JSON.stringify(_context)) {
        this.contextUpdateService.deleteContextFromLocalStorage(
          'previousContext',
        );
        this.contextUpdateService.setContextInLocalStorage(
          'previousContext',
          previousContext,
        );
      }
    }

    this.contextUpdateService.setContextInLocalStorage(
      type,
      _context as BreadCrumbConfig,
    );

    this.updateContext(status);
  }

  printContextCookies() {
    console.log(
      'Previous: ' +
        this.contextUpdateService.getContextFromLocalStorage('previousContext'),
    );
    console.log(
      'Current: ' +
        this.contextUpdateService.getContextFromLocalStorage('currentContext'),
    );
  }

  // Loops through the path from root till the current component and constructs a URL structure by joining pieces from these route objects
  getActiveRoutePathStructure(activeComponentRoute: ActivatedRoute) {
    let finalPath = '';
    let routeTraversalPath = activeComponentRoute?.pathFromRoot;
    for (let i = 0; i < routeTraversalPath.length; i++) {
      let router = routeTraversalPath[i];
      if (router?.routeConfig && router?.routeConfig?.path) {
        if (finalPath.length) {
          finalPath += '/';
        }
        finalPath += router?.routeConfig?.path;
      }
    }
    return finalPath;
  }

  // A depth first traversal from the root of the application to the currently active page
  // While doing this, we have to construct the path which was traversed to show the address of the component
  // using titles and levels from the app router config
  // We also need to store the name of the active component (which is our currently open component in the primary router)
  // because for a few components, we need to re-route the URL to a safe URL instead of just an updated URL with the
  // new breadcrumbs applied. The reason for this is that for every possible configuration of the breadcrumb, the URL might
  // be constructed and redirect to but it would not always be valid (or have data for what's requested)
  setRouteMetaData(root) {
    let lastLevelItem;
    while (root) {
      if (root.children && root.children.length) {
        root = root?.children[0];
        const current = root;
        this.activeComponentRoute = current;
        this.activeComponentName = current?.component?.name;
        if (
          current.hasOwnProperty('data') &&
          current.data.hasOwnProperty('level')
        ) {
          if (lastLevelItem !== current?.data?.title) {
            if (current?.data?.level === 1) {
              this.pagePath.parent = current?.data?.title;
            } else {
              this.pagePath.children.push(current?.data?.title);
            }
          }
          lastLevelItem = current?.data?.title;
          if (current.data.hasOwnProperty('preventSanitisation')) {
            this.preventSanitisation = current?.data?.preventSanitisation;
          } else {
            this.preventSanitisation = false;
          }
        }
      } else if (root.data && root.data.title && root.params) {
        this.URLParams = root;
        this.title = root.data.title;
        return;
      } else {
        return;
      }
    }
  }

  // Takes in the current URL and constructs the breadcrumb object by
  // fetching in variable data for :stackName, :clusterId, :resourceType & :resource from the active URL
  async processURL(URL) {
    const config = new BreadCrumbConfig();
    config.blueprint = await this.getBlueprint();
    config.environment = await this.getEnvironment(config);
    config.clusterId = this.URLParams?.params?.clusterId;
    config.resourceType = this.getResourceType(config);
    config.resource = this.getResource();
    if (this.lastFetchedFor !== config.blueprint) {
      config.branch = (await this.getBranch(config)) as Branch;
    } else {
      config.branch = this.branchData;
    }
    this.breadcrumbConfig = config;
    this.branchSelectorNeeded =
      this.featureFlagsService.getBranchSelectorFlag() &&
      this.isBranchSelectorNeeded(URL);

    if (config.resource) {
      this.pagePath.context = 'Resource';
    } else if (config.environment) {
      this.pagePath.context = 'Environment';
    } else if (config.blueprint) {
      this.pagePath.context = 'Blueprint';
    }
    if (config.resource) {
      if (await this.isResourceInvalid(config)) {
        throw new Error('resource');
      }
    } else if (
      this.isBranchSelectorNeeded(URL) && // URL has blueprint-designer in it
      !this.featureFlagsService.getBranchSelectorFlag() && // Feature has NOT been enabled
      config.branch.selectedBranch != config.branch.defaultBranch // current branch and default branch are not the same
    ) {
      throw new Error('branch');
    }
  }

  // Checks if branch selector is needed or not.
  // Currently the function only checks if blueprint-designer is
  // present inside the URL or not but more conditions can be added on here later
  isBranchSelectorNeeded(URL: string) {
    return (
      this.breadcrumbConfig?.branch?.selectedBranch &&
      URL?.includes('/blueprint-designer/')
    );
  }

  checkResourcesInvalid(resourcesMap, config) {
    return (
      !resourcesMap.hasOwnProperty(config?.resourceType) ||
      !resourcesMap[config?.resourceType]?.find(
        (resource) => resource == config?.resource,
      )
    );
  }

  async isResourceInvalid(config: BreadCrumbConfig) {
    let invalid = await new Promise((resolve) => {
      if (this.branchSelectorNeeded) {
        this.uiDropdownsControllerService
          .getResourceByStackUsingGET({
            stackName: config?.blueprint,
            branch: config?.branch?.selectedBranch,
            resourceType: config?.resourceType,
            resourceName: config?.resource,
          })
          .subscribe(
            (resource) => {
              resolve(false);
            },
            (error) => {
              resolve(true);
            },
          );
      } else if (config?.blueprint && config?.environment) {
        this.uiDropdownsControllerService
          .getAllResourcesByClusterUsingGET({
            clusterId:
              this.environmentIDMap[
                config?.blueprint + '_' + config?.environment
              ],
            includeSubstack: true,
            excludeAddOns: false,
            includeContent: false,
          })
          .subscribe((resources) => {
            const resourceTypeMap = {};
            resources?.forEach((resource) => {
              if (!resourceTypeMap.hasOwnProperty(resource?.resourceType)) {
                resourceTypeMap[resource?.resourceType] = [];
              }
              resourceTypeMap[resource?.resourceType].push(
                resource?.resourceName,
              );
            });

            resolve(this.checkResourcesInvalid(resourceTypeMap, config));
          });
      } else {
        this.resourceTypeSubscription$ = this.uiDropdownsControllerService
          .getAllResourcesByStackUsingGET({
            stackName: config?.blueprint,
            excludeAddOns: false,
          })
          .subscribe((resources) => {
            const resourceMap = {};
            resources.forEach((resource) => {
              if (resource?.resourceType?.toLowerCase() != 'unknown') {
                if (resourceMap?.hasOwnProperty(resource?.resourceType)) {
                  resourceMap[resource?.resourceType].push(
                    resource?.resourceName,
                  );
                } else {
                  resourceMap[resource?.resourceType] = [
                    resource?.resourceName,
                  ];
                }
              }
            });
            resolve(this.checkResourcesInvalid(resourceMap, config));
          });
      }
    });
    return invalid;
  }

  // Gets the name of the blueprint from the URL
  async getBlueprint() {
    if (this.URLParams.params.hasOwnProperty('stackName')) {
      if (
        !this.blueprintEnvironments.hasOwnProperty(
          this.URLParams?.params?.stackName,
        )
      ) {
        await this.getClusterAndBlueprintMapping();
      }
      if (
        !this.allBlueprints.find(
          (bp) => bp == this.URLParams?.params?.stackName,
        )
      ) {
        throw new Error('blueprint');
      }
      return Promise.resolve(this.URLParams?.params?.stackName);
    }
    return Promise.resolve('');
  }

  // Gets the ID of the environment from the URL and then using the allEnvironments object, filters out the name for the
  // corresponding ID [URL uses env ID, breadcrumb uses env NAME]
  async getEnvironment(currentConfig: BreadCrumbConfig) {
    if (this.URLParams.params.hasOwnProperty('clusterId')) {
      this.initAllEnvironmentsForBlueprint(currentConfig?.blueprint);
      this.initResourceTypes(
        {
          ID: this.URLParams?.params?.clusterId,
          name: null,
        },
        currentConfig?.blueprint,
      );
      if (this.allEnvironments) {
        if (
          !this.allEnvironments.find(
            (env) => env.id == this.URLParams?.params?.clusterId,
          )
        ) {
          await this.getClusterAndBlueprintMapping();
          this.initAllEnvironmentsForBlueprint(currentConfig?.blueprint);
        }
        if (
          !this.allEnvironments?.find(
            (env) => env.id == this.URLParams?.params?.clusterId,
          )
        ) {
          throw new Error('environment');
        }
        return Promise.resolve(
          this.allEnvironments.filter(
            (env) => env.id == this.URLParams?.params?.clusterId,
          )[0]?.name,
        );
      }
      return Promise.resolve(this.URLParams?.params?.clusterId);
    }
    return Promise.resolve('');
  }

  // Gets the name of the resource type from the URL
  // Once found, it initializes the list of the resources corresponding to that resource type
  getResourceType(currentConfig: BreadCrumbConfig) {
    if (this.URLParams.params.hasOwnProperty('resourceType')) {
      if (this.URL.includes('blueprint-designer')) {
        this.currentResourceMap = {};
      }
      this.initResources(this.URLParams.params.resourceType, currentConfig);
      return this.URLParams.params.resourceType;
    }

    if (this.URLParams.data.hasOwnProperty('resourceType')) {
      this.initResources(this.URLParams.data.resourceType, currentConfig);
      return this.URLParams.data.resourceType;
    }
    return '';
  }

  // Gets the name of the resource from the URL
  getResource() {
    if (this.URLParams.params.hasOwnProperty('resourceName')) {
      return this.URLParams?.params?.resourceName;
    }
    return '';
  }

  async getAllBranches(blueprint: string) {
    return new Promise((resolve) => {
      this.fetchingBranches = true;
      this.blueprintDesignerService
        .listBranchesUsingGET(blueprint)
        .subscribe(
          (data) => {
            resolve(data);
          },
          (error) => {
            console.log(error);
            resolve({});
          },
        )
        .add(() => {
          this.fetchingBranches = false;
        });
    });
  }

  lastFetchedFor: string; // Name of the last blueprint for which branches were fetched
  fetchingBranches: boolean = false; // Flag to mark if fetching branches API is in action
  branchData: any;
  async getBranch(config: BreadCrumbConfig) {
    return new Promise(async (resolve) => {
      if (!this.URL.includes('/blueprint-designer/')) {
        resolve('');
      } else {
        let selectedBranch: string;
        if (this.URLParams.params.hasOwnProperty('branchName')) {
          selectedBranch = this.URLParams?.params?.branchName;
          this.contextUpdateService.setLastOpenedBranchInLocalStorage(
            config?.blueprint,
            selectedBranch,
          );
        }
        let allBranches: Branch;
        if (this.featureFlagsService.getBranchSelectorFlag()) {
          allBranches = (await this.getAllBranches(config?.blueprint)) as any;
          allBranches.selectedBranch =
            selectedBranch || allBranches?.defaultBranch;
        } else {
          //Here we are currently selecting branch from url - need to see if we need to take it from stack object.
          allBranches = {
            branches: [selectedBranch],
            defaultBranch: selectedBranch,
            selectedBranch: selectedBranch,
          };
        }
        this.lastFetchedFor = config.blueprint;
        this.branchData = allBranches;
        resolve(allBranches);
      }
    });
  }

  // Receives branch name from the branch selector component and updates
  // the current context based on it.
  updateBranchInContext(branchName: string) {
    this.nextBreadcrumbConfig = this.breadcrumbConfig;
    this.nextBreadcrumbConfig.branch.selectedBranch = branchName;
    this.contextUpdateService.setLastOpenedBranchInLocalStorage(
      this.nextBreadcrumbConfig.blueprint,
      branchName,
    );
    this.applyBreadcrumbConfig();
    this.setNextBreadcrumbConfig(
      'blueprint',
      this.nextBreadcrumbConfig.blueprint,
    );
  }

  // Pretty print breadcrumb on console for debuggin purposes
  printBreadCrumb(config: BreadCrumbConfig) {
    console.log('-------------------');
    console.log('BLUEPRINT:', config?.blueprint);
    console.log('ENVIRONMENT:', config?.environment);
    console.log('RESOURCE TYPE:', config?.resourceType);
    console.log('RESOURCE:', config?.resource);
    console.log('-------------------');
  }

  // Clears all the filters inside the context switcher
  // when the next breadcrumb is applied or the URL is updated
  // This function uses onModelChange with empty string as search key
  clearFilters() {
    if (!this.nextBreadcrumbConfig) {
      return;
    }
    Object.keys(this.nextBreadcrumbConfig).forEach((configKey) => {
      let allOptionsRef;
      switch (configKey) {
        case 'blueprint':
          allOptionsRef = this.allBlueprints;
          break;
        case 'environment':
          allOptionsRef = this.allEnvironments;
          break;
        case 'resourceType':
          allOptionsRef = this.allResourceTypes;
          break;
        case 'resource':
          allOptionsRef = this.allResources;
          break;
      }
      const optionalName = configKey === 'environment' ? 'name' : null;
      this.onModelChange('', configKey, allOptionsRef, optionalName);
    });
  }

  // Updates the needed observable whenever there's something being searched for
  onModelChange(
    value: string,
    configType: string,
    allOptions: any[],
    customParam?: string,
  ) {
    this.filteredNgModelOptions$[configType] = of(
      this.filter(value, allOptions, customParam),
    );
  }

  // Filters out the data against the search string
  // There's an optional customParam params for searching using an attribute for object based lists
  filter(value: string, allOptions: any[], customParam?: string): string[] {
    if (!allOptions || !allOptions.length) {
      return ['Nothing available'];
    }
    const filterValue = value?.toLowerCase();
    const filteredOptions = allOptions.filter((optionValue) => {
      return customParam
        ? optionValue[customParam]?.toLowerCase()?.includes(filterValue)
        : optionValue?.toLowerCase()?.includes(filterValue);
    });
    return filteredOptions.length ? filteredOptions : ['No results found'];
  }

  // Updates the context switcher's state whenever there is a change in any of the config keys
  // and checks for the validity of the breadcrumb config (if whatever needed for the next breadcrumb is set or not)
  setNextBreadcrumbConfig(configKey: string, value: string) {
    this.nextBreadcrumbConfig[configKey] = value;
    switch (configKey) {
      case 'blueprint':
        if (
          this.breadcrumbConfig?.resource?.length &&
          this.breadcrumbConfig?.resourceType?.length &&
          !this.breadcrumbConfig?.environment?.length
        ) {
          this.nextBreadcrumbConfig?.resourceType
            ? this.resetResourcesTypes(true)
            : this.resetResourcesTypes();
          this.nextBreadcrumbConfig.resource
            ? this.resetResources(true)
            : this.resetResources();
          this.initResourceTypes(
            {
              name: this.nextBreadcrumbConfig?.environment,
              ID: null,
            },
            this.nextBreadcrumbConfig?.blueprint,
          );
        } else {
          this.currentResourceMap = {};
          this.resetEnvironments();
          this.resetResourcesTypes();
          this.resetResources();
          this.initAllEnvironmentsForBlueprint(
            this.nextBreadcrumbConfig?.blueprint,
          );
        }
        break;
      //When environment is selected and if there is any resourceType and/or resource selected set preservePrevious to true in resetResourcesTypes and resetResources
      case 'environment':
        this.nextBreadcrumbConfig?.resourceType
          ? this.resetResourcesTypes(true)
          : this.resetResourcesTypes();
        this.nextBreadcrumbConfig.resource
          ? this.resetResources(true)
          : this.resetResources();
        this.initResourceTypes(
          {
            name: this.nextBreadcrumbConfig?.environment,
            ID: null,
          },
          this.nextBreadcrumbConfig?.blueprint,
        );
        this.nextBreadcrumbConfig['resource'] =
          this.breadcrumbConfig['resource'];
        this.nextBreadcrumbConfig['resourceType'] =
          this.breadcrumbConfig['resourceType'];
        break;
      case 'resourceType':
        this.resetResources();
        this.initResources(this.nextBreadcrumbConfig?.resourceType);
        break;
    }
    this.updateBreadcrumbValidity();
  }

  // Sets environments to []
  resetEnvironments() {
    if (this.environmentSearchTerm?.nativeElement?.value) {
      this.environmentSearchTerm.nativeElement.value = '';
    }
    this.nextBreadcrumbConfig.environment = '';
    this.allEnvironments = [];
    this.filteredNgModelOptions$.environment = of([]);
  }

  // Sets resource types to []
  resetResourcesTypes(preservePrevious?) {
    preservePrevious
      ? (this.previousResourceType = this.nextBreadcrumbConfig.resourceType)
      : (this.previousResourceType = null);
    if (this.resourceTypeSearchTerm?.nativeElement?.value) {
      this.resourceTypeSearchTerm.nativeElement.value = '';
    }
    this.nextBreadcrumbConfig.resourceType = '';
    this.allResourceTypes = [];
    this.filteredNgModelOptions$.resourceType = of([]);
  }

  // Sets resources to []
  resetResources(preservePrevious?) {
    preservePrevious
      ? (this.previousResource = this.nextBreadcrumbConfig.resource)
      : (this.previousResource = null);
    if (this.resourceSearchTerm?.nativeElement?.value) {
      this.resourceSearchTerm.nativeElement.value = '';
    }
    this.nextBreadcrumbConfig.resource = '';
    this.allResources = [];
    this.filteredNgModelOptions$.resource = of([]);
  }

  // Clears the context switcher's data
  clearNewBreadCrumbConfig() {
    this.nextBreadcrumbConfig = new BreadCrumbConfig();
    this.resetEnvironments();
    this.resetResourcesTypes();
    this.resetResources();
    this.updateBreadcrumbValidity();
  }

  // Updates breadcrumb's validity
  // Uses a promise to break & return flag from the forEach loop
  // Update: Validity has been updated because environment and resource context switcher dropdowns
  // have been segregated now. Validity would only tell if the user has selected an env or a
  // valid resource combination now.
  async updateBreadcrumbValidity() {
    const validity$ = new Promise((resolve) => {
      if (
        this.showEnvironmentCS &&
        !this.nextBreadcrumbConfig['environment']?.length
      ) {
        resolve(false);
      } else if (
        !this.showEnvironmentCS &&
        (!this.nextBreadcrumbConfig['resourceType']?.length ||
          !this.nextBreadcrumbConfig['resource']?.length)
      ) {
        resolve(false);
      } else {
        resolve(true);
      }
    });
    this.isValidBreadcrumb = (await validity$) as boolean;
  }

  // First step is to sanitise the URL and get back a safe URL to which the application can be routed to
  // (sanitisation is important because for some URL + new Breadcrumb's context, the new URL might not be valid or have real data)
  // Second step is to loop through the keys and replace the current context with the new context
  // Third step is to navigate the application to the new URL
  // Fourth step is to set the current breadcrumb to the new breadcrumb (which just got applied)
  applyBreadcrumbConfig() {
    const currentURL = this.activeComponentURLStructure;
    const sanitizedData = this.getSanitizedURL(
      currentURL,
      this.nextBreadcrumbConfig,
    );

    const sanitizedURL = sanitizedData?.URL;
    const options = sanitizedData?.options;
    const queryParams = sanitizedData?.queryParams;
    let updatedURL: string;
    updatedURL = sanitizedURL;
    let contextConfigMap = {
      blueprint: ':stackName',
      environment: ':clusterId',
      resourceType: ':resourceType',
      resource: ':resourceName',
      branch: ':branchName',
    };
    Object.keys(this.nextBreadcrumbConfig)?.forEach((breadcrumbKey) => {
      let contextUrlKey = contextConfigMap[breadcrumbKey];
      //if url contains resource ID, replace the blueprint name and :resourceID param with proper resource name and type
      if (breadcrumbKey === 'environment') {
        if (this.nextBreadcrumbConfig?.environment?.length) {
          let key =
            this.nextBreadcrumbConfig?.blueprint +
            '_' +
            this.nextBreadcrumbConfig?.environment;
          updatedURL = updatedURL.replace(
            contextUrlKey,
            this.environmentIDMap[key],
          );
        }
      } else if (breadcrumbKey === 'branch') {
        updatedURL = updatedURL.replace(
          contextUrlKey,
          this.contextUpdateService.getLastBranchForBlueprint(
            this.nextBreadcrumbConfig.blueprint,
          ),
        );
      } else {
        updatedURL = updatedURL.replace(
          contextUrlKey,
          this.nextBreadcrumbConfig[breadcrumbKey],
        );
      }
    });
    updatedURL = this.refillRemainingParams(
      updatedURL,
      (this.URLParams.paramMap as any)?.params,
    );
    //the below part is done to avoid a bug in x6 which does not center graph node after going to a route
    //we need to first take user to loading route and then to actual route to destroy and recreate the graph
    //for this we check if breadcrumb config contains resource name and type but not environment
    //this indicates we are dealing with blueprint designer path
    if (
      this.nextBreadcrumbConfig?.resource &&
      this.nextBreadcrumbConfig?.resourceType &&
      !this.nextBreadcrumbConfig?.environment
    ) {
      setTimeout(() => {
        this.router.navigate([updatedURL]);
      }, 10);
      this.router.navigate([
        `stack/${
          this.nextBreadcrumbConfig?.blueprint
        }/blueprint-designer/${this.contextUpdateService.getLastBranchForBlueprint(
          this.nextBreadcrumbConfig?.blueprint,
        )}/${this.nextBreadcrumbConfig?.resourceType}/${
          this.nextBreadcrumbConfig?.resource
        }/loading`,
      ]);
    } else {
      this.router.navigate([updatedURL], {
        state: options,
        queryParams: queryParams,
      });
    }
    this.lastContextChangesUsingBreadcrumbs = true;
    this.breadcrumbConfig = { ...this.nextBreadcrumbConfig };
    this.updateCurrentContextInCookies(
      'currentContext',
      'optimistic',
      this.breadcrumbConfig,
    );
    this.sidebarStateService.sendSidebarStateUpdate('expand');
    this.contextSwitcherOpen = false;
    return;
  }

  // URL params apart from the 4 breadcrumb context keys related params need to be
  // filled back in wherever needed, this function injects all those values back into this (possibly inchoate) url
  refillRemainingParams(URL: string, params: ParamMap) {
    if (!params) {
      return URL;
    }
    Object.keys(params).forEach((param) => {
      if (URL.includes(`:${param}`)) {
        URL = URL.replace(`:${param}`, params[param]);
      }
    });
    return URL;
  }

  // Does what it says
  redirectToHome() {
    this.router.navigate(['/home']);
  }

  // For component X if the safeURL should land on any other component Y, component X needs to be added as a case
  // and the path for component Y should become the safeURL [this url will contain the current context and NOT the one which it needs to apply]
  // If the current component doesn't fall in the special case category, default would just make the safeURL as the current URL
  getSanitizedURL(
    url: string,
    breadcrumbConfig: BreadCrumbConfig,
  ): { URL: string; options: any; queryParams: any } {
    if (this.preventSanitisation) {
      return {
        URL: url,
        options: null,
        queryParams: null,
      };
    }
    let componentName = this.getComponentNameByURL(url);
    let safeURL: string;
    let options: any;
    let queryParams: any;
    switch (componentName) {
      case 'ClusterCreateUpdateComponent':
        safeURL = `/stack/:stackName/dialog/cluster/:clusterId/overview-v2`;
        options = {
          redirectToEdit: true,
        };
        break;

      case 'ClusterCreateUpdateComponentFromBlueprintOverview':
        safeURL = '/stack/:stackName';
        options = {
          redirectToEdit: true,
          redirectEnvID:
            this.environmentIDMap[
              this.nextBreadcrumbConfig?.blueprint +
                '_' +
                this.nextBreadcrumbConfig?.environment
            ],
        };
        break;

      case 'ResourceDiffCompare':
        safeURL =
          this.breadcrumbConfig?.blueprint !=
          this.nextBreadcrumbConfig?.blueprint
            ? '/:stackName/cluster/:clusterId/resources/type/:resourceType/resource/:resourceName/diff'
            : url;
        break;

      case 'ResourceSomethingComponent':
        safeURL = `/:stackName/cluster/:clusterId/resources/type/:resourceType/resource/:resourceName/:resourceName`;
        options = {
          redirectTo:
            this.pagePath?.children[this.pagePath?.children?.length - 1],
        };
        break;

      case 'ArtifactEditComponent':
        safeURL = `/stack/:stackName/artifacts/ci`;
        break;

      case 'BlueprintDesigner':
        safeURL = url;
        options = {
          mode: 'GRAPH',
        };
        break;

      // case 'BlueprintDesigner_ResourceOverview':
      //   safeURL = 'stack/:stackName/blueprint-designer';
      //   queryParams = {
      //     mode: 'TABLE',
      //   };
      //   break;

      default:
        safeURL = url;
    }
    return {
      URL: safeURL,
      options: options,
      queryParams: queryParams,
    };
  }

  // Since angular minifies the name of the component post prod deployment,
  // we need to conclude the name of the component based on its url structure
  // NOTE: Careful with the order of if conditions inside the function as we are doing substring based comparison
  getComponentNameByURL(url: string) {
    if (
      url.includes(
        '/:stackName/cluster/:clusterId/resources/type/application/resource/:resourceName',
      )
    ) {
      return 'ResourceSomethingComponent';
    }
    if (url.includes('/diff/compare/envs/')) {
      return 'ResourceDiffCompare';
    }
    if (url.includes('stack/:stackName/artifacts/edit-ci')) {
      return 'ArtifactEditComponent';
    }
    if (
      url.includes('stack/:stackName/blueprint-designer/:branchName/resource/')
    ) {
      return 'BlueprintDesigner_ResourceOverview';
    }
    if (url.includes('stack/:stackName/blueprint-designer')) {
      return 'BlueprintDesigner';
    }

    return 'default';
  }

  // Toggles the dropdown
  // stopPropagation is required as the click handlers are nested
  showEnvironmentCS: boolean = false;
  prevContextKey: string;
  toggleContextSwitcher(event, contextKey) {
    this.showEnvironmentCS = contextKey === this.ENVIRONMENT;
    this.contextSwitcherOpen = !(
      this.contextSwitcherOpen && this.prevContextKey === contextKey
    );
    this.prevContextKey = contextKey;
    event.stopPropagation();
  }

  // When branch selector is opened
  // context switcher should close on its own
  // and vice versa
  closeContextSwitcher() {
    this.contextSwitcherOpen = false;
  }

  // On cancelling the context switch, the switcher needs to go back to its original state
  // When resource switcher is updated and cancelled, we need to mark it back with the
  // previous state of breadcrumbs
  cancelContextSwitch() {
    this.nextBreadcrumbConfig = { ...this.breadcrumbConfig };
    if (!this.showEnvironmentCS) {
      this.setNextBreadcrumbConfig(
        'resourceType',
        this.breadcrumbConfig['resourceType'],
      );
      this.setNextBreadcrumbConfig(
        'resource',
        this.breadcrumbConfig['resource'],
      );
    }
    this.contextSwitcherOpen = false;
  }

  routeToLink(event, link) {
    event?.stopPropagation();
    this.router.navigate([link]);
  }

  checkUserAccessToPage(roles) {
    return this.userService.hasSomeroles(roles);
  }

  navigateToCapcHome() {
    this.router.navigate(['/capc/home']);
  }

  onClickMoveProjects() {
    triggerAnalyticsEvent(`BREADCRUMBS_PROJECTS_PROJECT_REDIRECT`);
  }
}
